import AttendeeRows from './AttendeeRows';
import InlineCommandBar from './InlineCommandBar';
import FullComposeDockedBottom from './FullComposeDockedBottom';
import { fullComposeTitlePlaceholderText } from './FullComposeLeftPane.locstring.json';
import LocationRow from './LocationRow';
import TimeComponent from './TimeComponent';
import { Icon } from '@fluentui/react/lib/Icon';
import { observer } from 'mobx-react-lite';
import updateLastFocusedControl from 'owa-addins-editor-plugin/lib/actions/updateLastFocusedControl';
import { CategoriesContainer } from 'owa-calendar-categories';
import type { CharmSuggestionsItem } from 'owa-calendar-charm-suggestions-data';
import updateSubjectSelectionRange from 'owa-calendar-compose-form-store/lib/actions/updateSubjectSelectionRange';
import { getComposeCalendarColorScheme } from 'owa-calendar-compose-form-store/lib/selectors/getComposeCalendarColorScheme';
import isNewEvent from 'owa-calendar-compose-form-store/lib/selectors/isNewEvent';
import isInEditSeriesInstanceMode from 'owa-calendar-compose-form-store/lib/utils/isInEditSeriesInstanceMode';
import TitleAndSuggestions from 'owa-calendar-compose-form-view/lib/components/TitleAndSuggestions';
import subjectOnKeyDown from 'owa-calendar-compose-form-view/lib/utils/subjectOnKeyDown';
import getMasterCategoryListForFolder from 'owa-calendar-forms-common/lib/helpers/getMasterCategoryListForFolder';
import FullComposeEditorArea from './FullComposeEditorArea';
import RecurrenceComponent from './RecurrenceComponent';
import { TimeChangeActionType } from 'owa-calendar-helpers-types/lib/TimeChangeActionType';
import { OnlineMeetingElement } from 'owa-calendar-meeting-forms-common-view';
import { CharmControlColorPattern, CharmPickerBackground } from 'owa-charmcontrol';
import type { ColorScheme } from 'owa-color-utils/lib/ColorScheme';
import { themeFromColorScheme } from 'owa-color-utils/lib/colorUtils';
import { getCalendarThemedButtonStyle } from 'owa-component-styling-utils';
import { ControlIcons } from 'owa-control-icons';
import { owaDate, OwaDate } from 'owa-datetime';
import { LastFocusedControl } from 'owa-editor-addin-plugin-types/lib/utils/AddinViewState';
import { EmailReminderEntrySource, ReminderContainer } from 'owa-email-reminders';
import { isFeatureEnabled } from 'owa-feature-flags';
import loc from 'owa-localize';
import { lazyCanTravelToEvent, MobilityFullComposeRow } from 'owa-mobility-compose';
import { getOnlineMeetingIconName } from 'owa-online-meeting';
import * as React from 'react';
import SmartCharmPicker, {
    SmartCharmPickerHandle,
} from 'owa-calendar-compose-form-view/lib/components/SmartCharmPicker';
import {
    onCharmChanged,
    onReminderChanged,
    onSubjectChanged,
    updateDateTimes,
    setRecurrence,
    openComposeSchedulingAssistant,
    dismissCharmSuggestions,
} from 'owa-calendar-compose-form-actions';
import FullComposeStickyBottomBorder from './FullComposeStickyBottomBorder';
import CollabSpaceComposeRow from './CollabSpaceComposeRow';
import {
    getComposeEvent,
    getComposeViewState,
    getFullComposeViewState,
} from 'owa-calendar-compose-store-manager';
import { CharmInfo } from 'owa-calendar-charms-store';
import isRecurrenceSelectorInEditEnabled from 'owa-recurrence/lib/utils/isRecurrenceSelectorInEditEnabled';
import type RecurrenceType from 'owa-service/lib/contract/RecurrenceType';
import useSetInitialScrollTop from '../hooks/useSetInitialScrollTop ';

import styles from './FullComposeLeftPane.scss';
import classnamesBind from 'classnames/bind';
const classNames = classnamesBind.bind(styles);

export interface FullComposeLeftPaneProps {
    className: string;
    formId: string;
    targetWindow: Window;
}

export interface FullComposeLeftPaneHandle {
    focusToCharmPicker(): void;
}

export default observer(
    function FullComposeLeftPane(
        props: FullComposeLeftPaneProps,
        ref: React.Ref<FullComposeLeftPaneHandle>
    ) {
        const { className, formId, targetWindow } = props;
        const charmPickerRef = React.useRef<SmartCharmPickerHandle>();

        React.useImperativeHandle(
            ref,
            () => ({
                focusToCharmPicker(): void {
                    if (charmPickerRef.current) {
                        charmPickerRef.current.focus();
                    }
                },
            }),
            []
        );

        const [showCollabSpaceTeachingUI, setShowCollabSpaceTeachingUI] = React.useState<boolean>(
            false
        );

        const event = getComposeEvent(formId);
        let calendarColorScheme: ColorScheme = getComposeCalendarColorScheme(formId);
        const calendarTheme = themeFromColorScheme(calendarColorScheme);
        let fullComposeViewState = getFullComposeViewState(formId);
        let color = calendarColorScheme.accessibleText.msColorWhiteBackground;
        const onlineMeetingIcon = getOnlineMeetingIconName(event);
        const shouldShowRecurrenceComponent =
            !isInEditSeriesInstanceMode(event) && !isRecurrenceSelectorInEditEnabled();
        let calendarThemedButtonStyle = getCalendarThemedButtonStyle(
            calendarColorScheme,
            fullComposeViewState.isDarkMode
        );
        let categories = event.Categories ? [...event.Categories].reverse() : [];

        let showMobilityCompose = false;
        if (isFeatureEnabled('cal-mf-mobility')) {
            const canTravelToEvent = lazyCanTravelToEvent.tryImportForRender();

            if (canTravelToEvent) {
                showMobilityCompose = canTravelToEvent(event);
            }
        }

        // Fix for the issue, wherein inside the projection popout window, the left pane of
        // full compose scrolls off 10px more while rendering with multiple recipient pills.
        // This hook ensures that we set the scrollTop to 0 after initial mount.
        const setScrollRegionRef = useSetInitialScrollTop();

        const updateSelectionRange = React.useCallback(
            (selectionStart: number, selectionEnd: number) => {
                const { subjectViewState } = getFullComposeViewState(formId);
                if (
                    selectionStart != subjectViewState.selectionStart ||
                    selectionEnd != subjectViewState.selectionEnd
                ) {
                    updateSubjectSelectionRange(subjectViewState, selectionStart, selectionEnd);
                }
            },
            [formId]
        );

        const onSubjectFocus = React.useCallback(() => {
            updateLastFocusedControl(
                getFullComposeViewState(formId).addin,
                LastFocusedControl.SubjectLine
            );
        }, [formId]);

        const onTimeSuggestionClicked = React.useCallback(
            (start: OwaDate, end: OwaDate) => {
                // Update start/end to match the timezone of start end in the compose form
                // Otherwise, the start/end timezones will change when the suggestion is clicked
                const event = getComposeEvent(formId);
                start = owaDate(event.Start.tz, start);
                end = owaDate(event.End.tz, end);
                updateDateTimes(
                    formId,
                    { start: start, end: end },
                    TimeChangeActionType.TimeSuggestions
                );
            },
            [formId]
        );

        const onTimeSuggestionNotFound = React.useCallback(() => {
            openComposeSchedulingAssistant(formId);
        }, [formId]);

        const onSelectIcon = React.useCallback(
            (icon: CharmInfo) => {
                onCharmChanged(icon, formId);
            },
            [formId]
        );

        const reminderChanged = React.useCallback(
            (reminderMinutesBeforeStart: number) => {
                onReminderChanged(reminderMinutesBeforeStart, formId);
            },
            [formId]
        );

        /**
         * Sets the subject to match the provided icon
         */
        const changeSubjectFromSuggestion = React.useCallback(
            (charmSuggestionsItem: CharmSuggestionsItem) => {
                onSubjectChanged(null, charmSuggestionsItem.keyword, formId);
            },
            [formId]
        );

        /**
         * Handles key presses on the subject control
         */
        const onSubjectKeyDown = React.useCallback(
            (target: React.KeyboardEvent<any>) => {
                const charmSuggestionsViewState = getComposeViewState(formId)
                    .charmSuggestionsViewState;
                subjectOnKeyDown(
                    target,
                    dismissCharmSuggestions,
                    changeSubjectFromSuggestion,
                    charmSuggestionsViewState,
                    formId
                );
            },
            [formId]
        );

        const onSetRecurrence = React.useCallback(
            (recurrenceType: RecurrenceType) => {
                setRecurrence(recurrenceType, formId);
            },
            [formId]
        );

        const showCardLayout = isFeatureEnabled('mon-cal-showEditPaneInCards');

        return (
            <div
                className={classNames(className, styles.container, {
                    showCardLayout: showCardLayout,
                })}>
                <div
                    className={classNames(styles.leftPane, 'customScrollBar', {
                        showCardLayout: showCardLayout,
                    })}
                    ref={setScrollRegionRef}>
                    <InlineCommandBar
                        event={event}
                        colorScheme={calendarColorScheme}
                        formId={formId}
                        targetWindow={targetWindow}
                    />
                    <div className={showCardLayout && styles.detailsContainer}>
                        <div className={styles.row}>
                            <div className={styles.charmPickerCell}>
                                <SmartCharmPicker
                                    ref={charmPickerRef}
                                    colorScheme={calendarColorScheme}
                                    buttonClassName={styles.charmIconButton}
                                    charmClassName={styles.charmIconTitle}
                                    resultContainerClassName={styles.editCharmContextMenuContainer}
                                    colorPattern={CharmControlColorPattern.Normal}
                                    background={CharmPickerBackground.Light}
                                    onSelectIcon={onSelectIcon}
                                    formId={formId}
                                />
                            </div>
                            <TitleAndSuggestions
                                className={styles.titleAndSuggestionsContainer}
                                inputStyle={{ color: color }}
                                autoFocus={true}
                                placeholder={loc(fullComposeTitlePlaceholderText)}
                                onKeyDown={onSubjectKeyDown}
                                formId={formId}
                                onFocus={onSubjectFocus}
                                shouldUpdateSelectionRange={true}
                                updateSelectionRange={updateSelectionRange}
                                onSelectText={updateSelectionRange}
                                onSelectCharmSuggestion={changeSubjectFromSuggestion}
                                colorScheme={calendarColorScheme}
                                charmSuggestionsClassName={styles.suggestionsContainer}
                                disabled={!fullComposeViewState.uiEnabled}
                            />
                            {event.Sensitivity === 'Private' && (
                                <Icon iconName={ControlIcons.Lock} className={styles.privateIcon} />
                            )}
                            <CategoriesContainer
                                categories={categories}
                                categoriesContainer={classNames(styles.categoriesContainer, {
                                    hasCategories: categories.length > 0,
                                })}
                                categoryList={getMasterCategoryListForFolder(event.ParentFolderId)}
                            />
                        </div>
                        <AttendeeRows
                            colorScheme={calendarColorScheme}
                            theme={calendarTheme}
                            formId={formId}
                        />
                        <TimeComponent
                            event={event}
                            onTimeSuggestionClicked={onTimeSuggestionClicked}
                            onTimeSuggestionNotFound={onTimeSuggestionNotFound}
                            calendarTheme={calendarTheme}
                            calendarThemedButtonStyle={calendarThemedButtonStyle}
                            calendarColorScheme={calendarColorScheme}
                            formId={formId}
                            targetWindow={targetWindow}
                        />
                        {shouldShowRecurrenceComponent && (
                            <div className={classNames(styles.row, styles.recurrenceRow)}>
                                <Icon
                                    iconName={ControlIcons.SyncOccurence}
                                    className={styles.recurrenceIcon}
                                />
                                <RecurrenceComponent
                                    colorScheme={calendarColorScheme}
                                    className={styles.recurrenceComponent}
                                    event={event}
                                    onRecurrenceChangedCallback={onSetRecurrence}
                                    isNewEvent={isNewEvent(formId)}
                                    disabled={!fullComposeViewState.uiEnabled}
                                    formId={formId}
                                    targetWindow={targetWindow}
                                />
                            </div>
                        )}
                        <LocationRow
                            calendarColorScheme={calendarColorScheme}
                            theme={calendarTheme}
                            formId={formId}
                        />
                        {showMobilityCompose && (
                            <div className={styles.row}>
                                <MobilityFullComposeRow event={event} entrySource={'FullCompose'} />
                            </div>
                        )}

                        {isFeatureEnabled('cal-mf-roomBookingAssistant') && (
                            <div className={styles.row}>
                                <Icon
                                    iconName={onlineMeetingIcon}
                                    className={styles.onlineMeetingIcon}
                                />
                                <OnlineMeetingElement
                                    event={event}
                                    calendarColorScheme={calendarColorScheme}
                                    theme={calendarTheme}
                                    compactMode={true}
                                    hideDropdownTitleIcon={true}
                                    disabled={!fullComposeViewState.uiEnabled}
                                    dropdownContainerClassName={
                                        styles.onlineMeetingDropdownContainer
                                    }
                                    hideLinkIcon={true}
                                    joinOnlineLinkContainerClassName={
                                        styles.joinOnlineLinkContainer
                                    }
                                    joinOnlineLinkClassName={styles.joinOnlineLink}
                                    formId={formId}
                                />
                            </div>
                        )}

                        <div className={styles.row}>
                            <Icon iconName={ControlIcons.AlarmClock} className={styles.alarmIcon} />
                            <ReminderContainer
                                event={event}
                                calendarColorScheme={calendarColorScheme}
                                onReminderChanged={reminderChanged}
                                entrySource={EmailReminderEntrySource.FullCompose}
                                disabled={!fullComposeViewState.uiEnabled}
                                viewStateId={formId}
                            />
                        </div>

                        <div className={classNames(styles.row, styles.bodyContainer)}>
                            <Icon iconName={ControlIcons.List} className={styles.descriptionIcon} />
                            {!fullComposeViewState.uiEnabled ? (
                                // This content gets sanitized after it comes in from
                                // setDataAsyncApi or the ReactEditor- which makes this safe against
                                // potential XSS attacks.
                                /* eslint-disable react/no-danger */
                                // tslint:disable-next-line:react-no-dangerous-html
                                <div
                                    dangerouslySetInnerHTML={{
                                        __html: fullComposeViewState.bodyViewState.content,
                                    }}
                                />
                            ) : (
                                /* eslint-enable react/no-danger */
                                <FullComposeEditorArea
                                    calendarColorScheme={calendarColorScheme}
                                    setShowCollabSpaceTeachingUI={setShowCollabSpaceTeachingUI}
                                    formId={formId}
                                    targetWindow={targetWindow}
                                />
                            )}
                        </div>
                        <FullComposeStickyBottomBorder formId={formId} />
                    </div>
                    <CollabSpaceComposeRow
                        event={event}
                        formId={formId}
                        showTeachingUI={showCollabSpaceTeachingUI}
                    />
                </div>
                <FullComposeDockedBottom formId={formId} targetWindow={targetWindow} />
            </div>
        );
    },
    { forwardRef: true }
);
